import React from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  acceptNewsletterPrompt,
  dismissNewsletterPrompt,
  selectShouldShowNewsletterPrompt,
} from '../../ducks/newsletterPrompt.duck';

import { IconClose, IconSpinner, InlineTextButton, PrimaryButton } from '../../components';

import css from './NewsletterOptInBanner.module.css';

// How long the success confirmation stays before the card removes itself.
const CONFIRMATION_MS = 4000;

const IDLE = 'idle';
const SUBMITTING = 'submitting';
const SUCCESS = 'success';
const ERROR = 'error';

/**
 * One-time newsletter opt-in prompt for users who registered before the
 * signup checkbox existed (so they have no newsletter preference on record).
 *
 * Base visibility comes from `selectShouldShowNewsletterPrompt`, but once the
 * user interacts we pin the card open with local `status` and drive the whole
 * flow inside the SAME card: spinner → success or error. That matters because
 * accepting writes the consent flag immediately, which flips the selector to
 * false — without pinning, the card would vanish mid-request and reappear as
 * a separate element, and a failure would unmount before its error could be
 * read.
 *
 * "Ja, meld meg på" is a clear affirmative action and counts as GDPR consent;
 * the server records the consent event (timestamp + user id) when subscribing.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName]
 * @param {string} [props.className]
 * @returns {JSX.Element|null}
 */
const NewsletterOptInBanner = props => {
  const { rootClassName, className } = props;
  const intl = useIntl();
  const dispatch = useDispatch();

  const shouldShow = useSelector(selectShouldShowNewsletterPrompt);

  const [status, setStatus] = React.useState(IDLE);
  const [errorId, setErrorId] = React.useState(null);
  const [retryable, setRetryable] = React.useState(true);
  const [hidden, setHidden] = React.useState(false);

  // Auto-dismiss only after a successful subscribe. Errors stay put so the
  // user can read them and retry.
  React.useEffect(() => {
    if (status !== SUCCESS) {
      return undefined;
    }
    const id = window.setTimeout(() => setHidden(true), CONFIRMATION_MS);
    return () => window.clearTimeout(id);
  }, [status]);

  // Pinned open from the first interaction until it resolves and auto-hides.
  const isInteracting = status !== IDLE;
  if (hidden || (!shouldShow && !isInteracting)) {
    return null;
  }

  const classes = classNames(rootClassName || css.root, className);

  const onAccept = () => {
    setStatus(SUBMITTING);
    setErrorId(null);
    dispatch(acceptNewsletterPrompt())
      .then(() => setStatus(SUCCESS))
      .catch(e => {
        // A permanently deleted ("forgotten") address can't be re-added via
        // the API at all — say so, and don't offer a retry that can only
        // fail again. Everything else is worth another attempt.
        const permanent = e?.apiError === 'cannot-resubscribe';
        setErrorId(
          permanent ? 'NewsletterOptInBanner.errorForgotten' : 'NewsletterOptInBanner.error'
        );
        setRetryable(!permanent);
        setStatus(ERROR);
      });
  };

  const onDismiss = () => {
    setHidden(true);
    dispatch(dismissNewsletterPrompt()).catch(() => {});
  };

  if (status === SUCCESS) {
    return (
      <div className={classNames(classes, css.confirmation)} role="status">
        <p className={css.confirmationText}>
          <FormattedMessage id="NewsletterOptInBanner.confirmation" />
        </p>
      </div>
    );
  }

  const submitting = status === SUBMITTING;

  return (
    <div
      className={classes}
      role="region"
      aria-label={intl.formatMessage({ id: 'NewsletterOptInBanner.heading' })}
      aria-busy={submitting}
    >
      {/* Same outcome as "Nei takk" — the prompt is one-time, so closing it
          is a decision, not a "remind me later". Exception: after a
          retryable error, closing only hides it for now (see onDismiss). */}
      <button
        type="button"
        className={css.closeButton}
        onClick={status === ERROR && retryable ? () => setHidden(true) : onDismiss}
        aria-label={intl.formatMessage({ id: 'NewsletterOptInBanner.closeAria' })}
      >
        <IconClose rootClassName={css.closeIcon} size="small" />
      </button>
      <div className={css.copy}>
        {status === ERROR ? (
          // No heading here: the user already answered "do you want the
          // newsletter?", so re-asking it above a failure reads oddly. The
          // error is the message now.
          <p className={css.error} role="alert">
            <FormattedMessage id={errorId} />
          </p>
        ) : (
          <>
            <p className={css.heading}>
              <FormattedMessage id="NewsletterOptInBanner.heading" />
            </p>
            <p className={css.subtext}>
              <FormattedMessage id="NewsletterOptInBanner.subtext" />
            </p>
          </>
        )}
      </div>
      <div className={css.actions}>
        {submitting ? (
          <span className={css.submitting} role="status">
            <IconSpinner rootClassName={css.submittingSpinner} />
            <FormattedMessage id="NewsletterOptInBanner.submitting" />
          </span>
        ) : (
          <>
            {status === ERROR && !retryable ? null : (
              <PrimaryButton type="button" className={css.acceptButton} onClick={onAccept}>
                <FormattedMessage
                  id={
                    status === ERROR
                      ? 'NewsletterOptInBanner.retry'
                      : 'NewsletterOptInBanner.accept'
                  }
                />
              </PrimaryButton>
            )}
            <InlineTextButton
              type="button"
              className={css.declineButton}
              // After a retryable error, closing shouldn't count as "no
              // thanks" — they wanted in, so hide for now and let the prompt
              // return. An unrecoverable error is dismissed for good, since
              // re-showing it would only nag with something they can't fix.
              onClick={status === ERROR && retryable ? () => setHidden(true) : onDismiss}
            >
              <FormattedMessage
                id={
                  status === ERROR
                    ? 'NewsletterOptInBanner.close'
                    : 'NewsletterOptInBanner.decline'
                }
              />
            </InlineTextButton>
          </>
        )}
      </div>
    </div>
  );
};

export default NewsletterOptInBanner;
